import type { ArtifactUsage, ReleaseSummary } from '@outpost/api-client';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FileCheck2, FileClock, FileX2, Rocket } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { CopyButton } from '@/components/copy-button';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { absoluteApiUrl } from '@/lib/config';
import { absoluteTime, formatBytes, relativeTime } from '@/lib/format';
import { RELEASE, withFilterValues } from '@/lib/query';

export function ReleasesPage() {
  const { projectId = '' } = useParams();
  const { client } = useAuth();

  const releases = useQuery({
    queryKey: ['releases', projectId],
    queryFn: () => client.listReleases(projectId),
  });
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId),
  });
  const usage = useQuery({
    queryKey: ['artifact-usage', projectId],
    queryFn: () => client.getArtifactUsage(projectId),
  });

  if (releases.isLoading) return <ListSkeleton rows={3} />;
  if (releases.isError) {
    return <ErrorState error={releases.error} onRetry={() => void releases.refetch()} />;
  }

  const command = uploadCommand(projectId);
  const orgId = project.data?.org_id;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Releases</h1>
        <p className="text-sm text-muted-foreground">
          Releases are created automatically from the <code>release</code> field on incoming events.
        </p>
        {usage.data && <UsageSummary usage={usage.data} />}
      </div>

      {releases.data?.length === 0 ? (
        <EmptyState
          icon={<Rocket className="size-5" />}
          title="No releases yet"
          description="Set `release` in your SDK options, then upload source maps for it so minified stack traces resolve."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          {releases.data?.map((release) => (
            <ReleaseRow key={release.id} projectId={projectId} release={release} />
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload source maps</CardTitle>
          <CardDescription>
            Run this as a CI step immediately after your production build, with the same release
            string the SDK reports. <code>OUTPOST_TOKEN</code> is an upload token, which you can
            create under{' '}
            {orgId ? (
              <Link
                to={`/organizations/${orgId}/settings#upload-tokens`}
                className="underline underline-offset-4"
              >
                organization settings
              </Link>
            ) : (
              'organization settings'
            )}
            ; keep it in your CI&apos;s secrets.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="scrollbar-thin overflow-x-auto rounded-md bg-muted p-4 text-xs leading-relaxed">
              <code>{command}</code>
            </pre>
            <div className="absolute right-2 top-2">
              <CopyButton value={command} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * One release, with a toggle for the files uploaded for it.
 *
 * The version links to the release's issues and the toggle is a separate
 * button beside it, rather than the whole row being a link: a button nested in
 * a link is not reachable as either by keyboard or screen reader.
 */
function ReleaseRow({ projectId, release }: { projectId: string; release: ReleaseSummary }) {
  const [expanded, setExpanded] = useState(false);
  const panelId = `release-files-${release.id}`;
  const files = release.artifact_count;

  return (
    <div className="border-b border-border last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
        <Link
          to={issuesFor(projectId, release.version)}
          title="Issues in this release"
          className="min-w-0 font-mono text-sm wrap-anywhere underline-offset-4 hover:underline"
        >
          {release.version}
        </Link>
        {release.dist && (
          <Badge variant="muted" className="shrink-0">
            {release.dist}
          </Badge>
        )}
        <SourceMapBadge release={release} />
        {release.artifact_count > 0 && (
          <span
            className="shrink-0 text-xs tabular-nums text-muted-foreground"
            title="Size of every file uploaded for this release"
          >
            {formatBytes(release.artifact_bytes)}
          </span>
        )}

        <span
          className="ml-auto shrink-0 whitespace-nowrap text-sm text-muted-foreground"
          title={absoluteTime(release.created_at)}
        >
          {relativeTime(release.created_at)} ago
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-muted-foreground"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${files} ${files === 1 ? 'file' : 'files'} uploaded for ${release.version}`}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? <ChevronDown /> : <ChevronRight />}
          {files} {files === 1 ? 'file' : 'files'}
        </Button>
      </div>

      <div id={panelId} hidden={!expanded}>
        {expanded && <Artifacts projectId={projectId} release={release} />}
      </div>
    </div>
  );
}

/**
 * The project's total, over every release rather than the page listed below.
 *
 * Counted as uploaded: a file shared by several releases counts in each, which
 * is what uploading less would change, not what storage physically holds.
 */
function UsageSummary({ usage }: { usage: ArtifactUsage }) {
  if (usage.release_count === 0) return null;
  const releases = usage.release_count;
  const files = usage.artifact_count;
  return (
    <p
      className="mt-1 text-sm text-muted-foreground"
      title={`${files} ${files === 1 ? 'file' : 'files'}, bundles included`}
    >
      Source maps:{' '}
      <span className="font-medium tabular-nums text-foreground">
        {formatBytes(usage.artifact_bytes)}
      </span>{' '}
      across {releases} {releases === 1 ? 'release' : 'releases'}
    </p>
  );
}

/**
 * Whether stack traces in this release can be mapped back to source.
 *
 * Only `.map` files count. A release with bundles but no maps still says so:
 * a bundle might carry its map inline, but nothing short of fetching it can
 * tell, and the common case is a build that forgot to emit them.
 */
function SourceMapBadge({ release }: { release: ReleaseSummary }) {
  const count = release.source_map_count;

  if (release.artifact_count === 0 && release.artifacts_removed_at) {
    return (
      <Badge
        variant="muted"
        className="shrink-0 gap-1"
        title={`Deleted ${absoluteTime(release.artifacts_removed_at)}, after the release had no activity for this project's stale release period. Its stack traces stay minified unless maps are uploaded again.`}
      >
        <FileClock className="size-3" aria-hidden />
        Source maps removed
      </Badge>
    );
  }

  if (count > 0) {
    return (
      <Badge variant="secondary" className="shrink-0 gap-1">
        <FileCheck2 className="size-3" aria-hidden />
        {count} source {count === 1 ? 'map' : 'maps'}
      </Badge>
    );
  }

  return (
    <Badge
      variant="muted"
      className="shrink-0 gap-1"
      title={
        release.artifact_count > 0
          ? 'Files were uploaded, but none of them is a .map. Stack traces stay minified unless a bundle carries its map inline.'
          : 'Nothing has been uploaded for this release, so its stack traces stay minified.'
      }
    >
      <FileX2 className="size-3" aria-hidden />
      No source maps
    </Badge>
  );
}

/** The files uploaded for a release, fetched only once someone opens it. */
function Artifacts({ projectId, release }: { projectId: string; release: ReleaseSummary }) {
  const { client } = useAuth();
  const empty = release.artifact_count === 0;

  const artifacts = useQuery({
    queryKey: ['artifacts', projectId, release.id],
    queryFn: () => client.listArtifacts(projectId, release.id),
    enabled: !empty,
  });

  return (
    <div className="space-y-2 border-t border-border bg-muted/20 px-3 py-3">
      {empty && release.artifacts_removed_at ? (
        <p className="text-sm text-muted-foreground">
          This release&apos;s files were deleted {relativeTime(release.artifacts_removed_at)} ago,
          after it had no activity for the period set in{' '}
          <Link to={`/projects/${projectId}/settings`} className="underline underline-offset-4">
            project settings
          </Link>
          . Events from it stay minified unless its source maps are uploaded again. Last active{' '}
          {relativeTime(release.last_seen_at)} ago.
        </p>
      ) : empty ? (
        <p className="text-sm text-muted-foreground">
          Nothing has been uploaded for this release. Run the command below with{' '}
          <code className="wrap-anywhere">--release &quot;{release.version}&quot;</code>
          {release.dist && (
            <>
              {' '}
              and <code className="wrap-anywhere">--dist &quot;{release.dist}&quot;</code>
            </>
          )}
          .
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {formatBytes(release.artifact_bytes)} in total
            {release.last_upload_at &&
              `, last uploaded ${relativeTime(release.last_upload_at)} ago`}
            , last active {relativeTime(release.last_seen_at)} ago. Stack frames are matched to
            these files by name: when a trace stays minified, compare a frame&apos;s{' '}
            <code>abs_path</code> with the names below.
          </p>

          {artifacts.isLoading && (
            <div className="space-y-1.5">
              {Array.from({ length: Math.min(release.artifact_count, 3) }, (_, index) => (
                <Skeleton key={index} className="h-7 w-full" />
              ))}
            </div>
          )}
          {artifacts.isError && (
            <ErrorState error={artifacts.error} onRetry={() => void artifacts.refetch()} />
          )}

          {artifacts.data && (
            <ul className="divide-y divide-border rounded-md border border-border bg-card">
              {artifacts.data.map((artifact) => (
                <li
                  key={artifact.id}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-1.5 text-xs"
                >
                  <span className="min-w-0 flex-1 font-mono wrap-anywhere">{artifact.name}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatBytes(artifact.size_bytes)}
                  </span>
                  <span
                    className="shrink-0 whitespace-nowrap text-muted-foreground"
                    title={absoluteTime(artifact.created_at)}
                  >
                    uploaded {relativeTime(artifact.created_at)} ago
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The CI step, pointed at this installation.
 *
 * The token is left as a variable on purpose: the command is copied into CI
 * configuration, which is exactly where a pasted secret would leak from.
 */
function uploadCommand(projectId: string): string {
  return `OUTPOST_API_URL=${absoluteApiUrl()} \\
OUTPOST_TOKEN=$OUTPOST_TOKEN \\
OUTPOST_PROJECT_ID=${projectId} \\
  bunx @peaky-ai/outpost-cli upload-sourcemaps \\
    --release "$GIT_SHA" \\
    --url-prefix "~/static/js" \\
    ./dist`;
}

/**
 * The issues view scoped to one release. It starts from the view's own
 * `is:unresolved` default, which a bare `release:` query would replace.
 */
function issuesFor(projectId: string, version: string): string {
  const query = withFilterValues('is:unresolved', RELEASE, [version]);
  return `/projects/${projectId}/issues?${new URLSearchParams({ query }).toString()}`;
}
